import React, { useState, useEffect, useRef } from "react";

import { triggerSnowplowImpressionEvent, triggerSnowplowVideoEvent } from "@themuse/snowplow-js-client";

import styles from "./JobIndividualBody.module.scss";

interface JWPlayerWindow extends Window {
    jwplayer: any;
}

declare const window: JWPlayerWindow;

interface JWPlayerVideoModuleProps {
    title?: string;
    description?: string;
    tagName: number | string;
    excludeTag?: boolean;
}

const installPlayerScript = ({ onLoadCallback, scriptSrc, uniqueScriptId }) => {
    const jwPlayerScript = document.createElement("script");
    jwPlayerScript.id = uniqueScriptId;
    jwPlayerScript.src = scriptSrc;
    jwPlayerScript.defer = true;
    jwPlayerScript.onload = onLoadCallback;
    document.head.appendChild(jwPlayerScript);
};

export const JWPlayerVideoModule = (props: JWPlayerVideoModuleProps) => {
    const {
        title, description, tagName, excludeTag
    } = props;
    const playerRef = useRef();
    const playerWrapperRef = useRef();
    const [playerScriptInitialized, setPlayerScriptInitialized] = useState(false);
    const [jwplayerInstance, setJwplayerInstance] = useState(null);
    const [trackingProps, setTrackingProps] = useState(null);
    const firstView = useRef(false);
    const exclude = excludeTag ? "&exclude_tags=Hiring+Manager+Video" : "";

    useEffect(() => {
        installPlayerScript({
            onLoadCallback: () => {
                setPlayerScriptInitialized(true);
            },
            scriptSrc: "https://cdn.jwplayer.com/libraries/OOgy074L.js",
            uniqueScriptId: "jdp-jwplayer-instance"
        });
    }, []);

    useEffect(() => {
        if (playerScriptInitialized) {
            const instance = window.jwplayer(playerRef.current).setup({
                playlist: `https://cdn.jwplayer.com/v2/playlists/5lLe5BiO?tags=${tagName}${exclude}`,
                mute: true
            });
            setJwplayerInstance(instance);
        }
    }, [playerScriptInitialized, exclude, tagName]);

    useEffect(() => {
        const dataTrackingProps = {
            sp_impression_component: "insert",
            sp_impression_insert_type: "media",
            sp_impression_position: "bottom",
            sp_search_id: "null",
            sp_media_type: "video",
            sp_media_id: null,
            sp_media_name: null,
            sp_media_provider_key: null,
            sp_media_provider_name: "jwplayer",
        };
        if (jwplayerInstance !== null) {
            jwplayerInstance.on("ready", () => {
                const playlist = jwplayerInstance.getPlaylist();
                setTrackingProps({
                    ...dataTrackingProps,
                    sp_media_id: playlist[0].mediaid,
                    sp_media_name: playlist[0].title,
                    sp_media_provider_key: `${playlist[0].feedid}-${tagName}`,
                });
            });
        }
    }, [jwplayerInstance, tagName]);

    useEffect(() => {
        if (jwplayerInstance && trackingProps) {
            jwplayerInstance.on("viewable", () => {
                // The viewable event is bugged, so we add a getViewable check
                if (!firstView.current && jwplayerInstance.getViewable()) {
                    triggerSnowplowImpressionEvent(playerWrapperRef.current);
                    firstView.current = true;
                }
            });
            jwplayerInstance.on("play", () => {
                triggerSnowplowVideoEvent("play", playerWrapperRef.current);
            });
        }
    }, [jwplayerInstance, trackingProps]);

    return (
        <div className={styles.jobBodyJWPlayerVideo} ref={playerWrapperRef} data-tracking-props={JSON.stringify(trackingProps)}>
            {title ? <h3>{title}</h3> : null}
            {description ? <p>{description}</p> : null}
            <div ref={playerRef as React.RefObject<HTMLDivElement>} />
        </div>
    );
};
